// #region License

/**
 * @license
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

// #endregion

import * as _ from "lodash";
import * as React from "react";
import { List, Popup } from "semantic-ui-react";
import "./ApplicationMenuItem.less";
import { ApplicationList, ApplicationListItem } from "../ApplicationList";
import { ApplicationMenuContext } from "./ApplicationMenuContext";
import { Link, matchPath, useLocation } from "../../services/router";
import { MenuBracketIcon } from "../../assets/icons/MenuBracket";
import { getApplicationClassName, getClassName } from "../utils/className";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const className = getApplicationClassName("menu", "item");
export const ApplicationMenuItem = props => {
  const {
    children,
    content,
    actions,
    title,
    items,
    icon,
    path,
    exact,
    link,
    name,
    loading,
    disabled,
    offset = 0,
    sortable,
    onClick,
    ...rest
  } = props;
  const {
    open,
    setOpen,
    item,
    setItem
  } = React.useContext(ApplicationMenuContext);
  const location = useLocation();
  const [active, setActive] = React.useState(false);
  const [selected, setSelected] = React.useState(false);
  const handleClick = React.useCallback(e => {
    if (setItem) {
      if (name && (!selected || open)) {
        React.startTransition(() => {
          setItem(name);
        });
      }
    }
    if (setOpen) {
      React.startTransition(() => {
        setOpen(!_.isEmpty(items));
      });
    }
    onClick?.(e, props);
  }, [props, name, open, items, selected, setItem, setOpen, onClick]);
  React.useEffect(() => {
    React.startTransition(() => {
      setSelected(_.startsWith(item, name));
    });
  }, [name, item]);
  React.useEffect(() => {
    React.startTransition(() => {
      setActive(!offset && path && !!matchPath({
        path,
        end: exact
      }, location.pathname));
    });
  }, [offset, path, exact, location]);
  const contentComponent = React.useMemo(() => /*#__PURE__*/_jsxs(List.Item, {
    as: link ? Link : "",
    to: path,
    "aria-label": children || content,
    "data-testid": name,
    className: getClassName(className, {
      active
    }),
    disabled: disabled,
    onClick: handleClick,
    style: {
      paddingLeft: `${offset + 1}em`
    },
    children: [/*#__PURE__*/React.isValidElement(icon) ? icon : /*#__PURE__*/_jsx(List.Icon, {
      name: icon
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        display: open ? "flex" : "none",
        width: "100%"
      },
      children: [/*#__PURE__*/_jsx(List.Content, {
        verticalAlign: "middle",
        children: children || content
      }), !_.isEmpty(items) && /*#__PURE__*/_jsx(MenuBracketIcon, {
        active: selected
      })]
    })]
  }), [active, offset, link, path, icon, loading, disabled, children, content, name, open, handleClick]);
  const itemsList = React.useMemo(() => !(_.isEmpty(items) || loading) && /*#__PURE__*/_jsx(ApplicationList, {
    items: items,
    sortable: sortable
    // eslint-disable-next-line react/jsx-props-no-spreading
    ,
    ...rest
  }), [items, loading, sortable, rest]);
  const itemComponent = React.useMemo(() => /*#__PURE__*/_jsx(ApplicationListItem, {
    path: path,
    name: name,
    link: link,
    content: content,
    title: title,
    offset: offset + 4,
    direction: "row",
    disabled: disabled,
    actions: actions
    // eslint-disable-next-line react/jsx-props-no-spreading
    ,
    ...rest
  }), [content, title, path, link, offset, actions, rest]);
  const popup = React.useMemo(() => /*#__PURE__*/_jsx(Popup, {
    trigger: contentComponent,
    disabled: !content || open || !!offset,
    position: "right center",
    children: content
  }), [open, offset, selected, content, contentComponent, itemsList]);
  const nestedComponent = React.useMemo(() => /*#__PURE__*/_jsxs(_Fragment, {
    children: [popup, !_.isEmpty(items) && /*#__PURE__*/_jsx("div", {
      className: getClassName({
        selected: selected && open
      }),
      children: selected && open && itemsList
    })]
  }), [popup, items, itemsList, selected, open]);
  return React.useMemo(() => _.isEmpty(items) && offset ? itemComponent : nestedComponent, [offset, items, itemComponent, nestedComponent]);
};